'use client';

import { CloseIcon } from '@/assets/icons/CloseIcon';
import ModalLoader from '@/assets/icons/ModalLoader';
import { routes } from '@/constants';
import { MODALS } from '@/constants/modals';
import { createClient } from '@/prismicio';
import { components } from '@/slices';
import useModalStore from '@/stores/modals';
import { ContentModalData, PrismicModalData } from '@/types/modals';
import { SliceZone as SliceZoneType } from '@prismicio/client';
import { SliceZone } from '@prismicio/react';
import { redirect } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  ModalSlice,
  PrivacyDocumentDataSlicesSlice,
} from '../../../prismicio-types';
import styles from './DialogModal.module.scss';

type ModalSliceType = null | SliceZoneType<
  ModalSlice | PrivacyDocumentDataSlicesSlice
>;

const DialogModal = () => {
  const [slices, setSlices] = useState<ModalSliceType>();

  const activeModals = useModalStore((state) => state.activeModals);
  const removeModal = useModalStore((state) => state.removeModal);

  const activeModal = activeModals?.at(-1);

  const handleCloseModal = () => {
    if (activeModal?.data?.handleResetFormValues) {
      activeModal.data.handleResetFormValues();
    }
    if (activeModal) {
      removeModal(activeModal?.name);
      setSlices(null);
    }
  };

  const disableClose = activeModal?.data?.disableClose === true;
  const canClose = !disableClose;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeModal && canClose) {
        if (activeModal?.data?.handleResetFormValues) {
          activeModal.data.handleResetFormValues();
        }
        removeModal(activeModal?.name);
        setSlices(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, canClose]);

  const currentModals = activeModals
    ?.map((active) => MODALS.find((m) => m.name === active.name))
    ?.filter((m) => !!m);

  const currentModal = currentModals?.at(-1);

  const activePrismicModal =
    currentModal && 'sliceType' in currentModal && 'uid' in currentModal
      ? (currentModal as PrismicModalData)
      : null;

  const [loading, setLoading] = useState(!!activePrismicModal);

  useEffect(() => {
    if (!activePrismicModal?.uid || !activePrismicModal?.sliceType) {
      if (activePrismicModal) setSlices(null);
      return;
    }

    let isCancelled = false;

    (async function () {
      try {
        setLoading(true);
        const client = createClient();
        const page = await client.getByUID(
          activePrismicModal.sliceType,
          activePrismicModal.uid
        );

        if (!isCancelled) {
          setSlices((page.data as { slices: ModalSliceType }).slices);
        }
      } catch {
        if (!isCancelled) {
          redirect(routes.notFound);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [activePrismicModal]);

  const topVisibleModal = MODALS.find((m) => m.name === activeModal?.name);

  const topVisibleContentModal =
    topVisibleModal && 'Component' in topVisibleModal
      ? (topVisibleModal as ContentModalData)
      : null;

  if (!activeModal) return null;

  return (
    <>
      {currentModals?.map((m, i) => {
        const currentModal = activeModals?.find((el) => el.name === m.name);
        return (
          <div
            onClick={() => {
              if (canClose && topVisibleModal?.closeOnOverlayClick)
                handleCloseModal();
            }}
            className={styles.modalWrapper}
            key={i}
            style={{
              zIndex: i + 901,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className={styles.content}
            >
              {canClose && (
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={styles.closeIconWrapper}
                >
                  <CloseIcon className={styles.icon} />
                </button>
              )}
              {loading ? (
                <div className={styles.loaderContainer}>
                  <ModalLoader />
                  <div className={styles.textContainer}>
                    <p className={styles.loaderTitle}>Please wait</p>
                    <p className={styles.loaderSubtitle}>
                      It might take a while...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {topVisibleContentModal?.Component ? (
                    <div
                      className={`${styles.wrapper} ${topVisibleContentModal && activeModal.name.startsWith('form') && styles.fixedWidth}`}
                    >
                      {topVisibleContentModal?.title && (
                        <p className={styles.title}>
                          {topVisibleContentModal?.title}
                        </p>
                      )}
                      <topVisibleContentModal.Component
                        {...currentModal?.data}
                      />
                    </div>
                  ) : activePrismicModal ? (
                    <>
                      {activePrismicModal.sliceType === 'privacy' ||
                      activePrismicModal.sliceType === 'tripointmodal' ? (
                        <div className={styles.contentWrapper}>
                          {slices && (
                            <SliceZone
                              components={components}
                              slices={slices}
                            />
                          )}
                        </div>
                      ) : (
                        <>
                          {slices && (
                            <SliceZone
                              components={components}
                              slices={slices}
                            />
                          )}
                        </>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default DialogModal;
